import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { useTheme } from '@/components/theme-provider';
import type { AuditSeverity, AuditSummary } from '@claro/shared';
import { AUDIT_SEVERITY_LABELS } from '@claro/shared';

const SEVERITY_COLORS: Record<AuditSeverity, string> = {
  0: '#10b981',
  1: '#3b82f6',
  2: '#f59e0b',
  3: '#f97316',
  4: '#ef4444',
  5: '#b91c1c',
};

interface SeverityDonutProps {
  summary: AuditSummary | undefined;
  isLoading: boolean;
  onSliceClick?: (severity: AuditSeverity) => void;
}

export function SeverityDonut({ summary, isLoading, onSliceClick }: SeverityDonutProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const option = useMemo<EChartsOption>(() => {
    if (!summary) return {};

    const data = ([0, 1, 2, 3, 4, 5] as AuditSeverity[])
      .filter(s => (summary.severityBreakdown[s] ?? 0) > 0)
      .map(s => ({
        value: summary.severityBreakdown[s],
        name: `${s} · ${AUDIT_SEVERITY_LABELS[s]}`,
        itemStyle: { color: SEVERITY_COLORS[s] },
        severity: s,
      }));

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const total = summary.matchedUnitCnpjPairs ?? summary.matched;
          const pct = total ? ((params.value / total) * 100).toFixed(1) : '0.0';
          return `${params.name}<br/>${params.value.toLocaleString('pt-BR')} vínculos (${pct}%)`;
        },
      },
      legend: { show: false },
      series: [
        {
          type: 'pie',
          radius: ['50%', '78%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          padAngle: 2,
          itemStyle: { borderRadius: 4 },
          label: {
            show: true,
            formatter: '{b}: {c}',
            fontSize: 10,
            color: isDark ? '#a1a1aa' : '#71717a',
          },
          emphasis: {
            label: { show: true, fontWeight: 'bold', fontSize: 12 },
            itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.3)' },
          },
          data,
        },
      ],
    };
  }, [summary, isDark]);

  if (isLoading) {
    return <div className="h-[280px] animate-pulse rounded-lg border bg-card" />;
  }

  if (!summary) return null;

  const handleClick = (params: any) => {
    if (onSliceClick && params.data?.severity != null) {
      onSliceClick(params.data.severity);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Distribuição por Severidade</h3>
      <ReactECharts
        option={option}
        style={{ height: 240 }}
        onEvents={{ click: handleClick }}
      />
    </div>
  );
}
